import { Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface ItemBadgeProps {
    value: string | number | JSX.Element;
    title: string;
    style?: React.CSSProperties;
    size?: 'small' | 'medium';
    icon?: JSX.Element;
    level?: number;
    percent?: number;
    iconColor?: string;
}

/**
 * Represents an item badge.
 *
 * @param {ItemBadgeProps} value - The value of the item badge.
 * @param {ItemBadgeProps} title - The title of the item badge.
 * @param {ItemBadgeProps} size - The size of the item badge.
 *
 * @returns {JSX.Element} The rendered item badge component.
 */
export default function ItemBadge({ value, title, size, style, icon, level, percent, iconColor }: ItemBadgeProps) {
    const theme = useTheme();

    // Get level color from theme
    const getLevelColor = (level?: number) => {
        if (!level) return theme.palette.primary.main;
        return theme.palette.levels?.[level as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    const levelColor = getLevelColor(level);

    return (
        <Chip
            icon={icon}
            label={value}
            title={title}
            size={size === 'small' ? 'small' : 'medium'}
            sx={{
                bgcolor: theme.palette.mode === 'dark' ? alpha(levelColor, 0.2) : alpha(levelColor, 0.1),
                color:
                    theme.palette.mode === 'dark' ? levelColor : theme.palette.getContrastText(alpha(levelColor, 0.8)),
                border: `1px solid ${alpha(levelColor, theme.palette.mode === 'dark' ? 0.3 : 0.2)}`,
                fontWeight: 500,
                height: '24px',
                borderRadius: '12px 1px',
                '& .MuiChip-icon': {
                    color: iconColor || levelColor,
                },
                ...style,
            }}
        />
    );
}

export function NewBadge() {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).layout;
    return (
        <ItemBadge value={langStrings.newBadgeTitle} title={langStrings.newBadgeHelper} style={{ marginBottom: 10 }} />
    );
}
